import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { isType } from '@sorare/core/src/gql';
import { range } from '@sorare/core/src/lib/arrays';

import { DecisiveActions_anyPlayer } from './__generated__/index.graphql';

const Relative = styled.div`
  position: relative;
`;

const Row = styled(Horizontal)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: var(--unit);
`;

const StyledStatIcon = styled(StatIcon)`
  background-color: var(--c-neutral-300);
  outline: 2px solid var(--c-neutral-300);
  border-radius: 50%;
  width: 20px;
  height: 20px;
`;

type Props = {
  player: DecisiveActions_anyPlayer;
};

export const DecisiveActions = ({ player }: Props) => {
  const { playerGameScores } = player;
  const playerGameScore = playerGameScores?.[0];
  if (!playerGameScore) return null;
  const { anyPlayerGameStats } = playerGameScore;
  if (!isType(anyPlayerGameStats, 'PlayerGameStats')) return null;

  const goals = anyPlayerGameStats?.goals || 0;
  const goalAssist = anyPlayerGameStats?.goalAssist || 0;
  const penaltiesSaved = anyPlayerGameStats?.penaltiesSaved || 0;
  const assistPenaltyWon = anyPlayerGameStats?.assistPenaltyWon || 0;
  return (
    <Relative>
      <Row center gap={0.5}>
        {range(penaltiesSaved).map(i => (
          <StyledStatIcon key={i} statKey="assist_penalty_won" />
        ))}
        {range(assistPenaltyWon).map(i => (
          <StyledStatIcon key={i} statKey="penalty_save" />
        ))}
        {range(goalAssist).map(i => (
          <StyledStatIcon key={i} statKey="goal_assist" />
        ))}
        {range(goals).map(i => (
          <StyledStatIcon key={i} statKey="goals" />
        ))}
      </Row>
    </Relative>
  );
};

DecisiveActions.fragments = {
  anyPlayer: gql`
    fragment DecisiveActions_anyPlayer on AnyPlayerInterface {
      slug
      playerGameScores(last: 1) {
        id
        score
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            goals
            goalAssist
            penaltiesSaved
            assistPenaltyWon
          }
        }
      }
    }
  ` as TypedDocumentNode<DecisiveActions_anyPlayer>,
};
